import React, { useRef, useEffect } from 'react'
import { useField } from 'formik'
import Input from 'antd/es/input'
import Inputmask from 'inputmask'
import { InputProps } from 'antd'
import { Show } from './Show'
import { TailSpin } from 'react-loader-spinner'
import styled from '@emotion/styled'
import { set } from 'date-fns'

export const TextField = React.memo(
  (
    props: InputProps & {
      name?: any
      mask?: any
      label?: any
    }
  ) => {
    const [field, meta] = useField(props.name)

    return (
      <div>
        <TextFieldInput
          {...field}
          {...props}
          onChange={(e: any) => {
            if (props.onChange) props.onChange(e)
            field.onChange(e)
          }}
        />
        <Show condition={!!meta.touched && !!meta.error}>
          <div style={{ textAlign: 'left', color: 'red' }}>{meta.error}</div>
        </Show>
      </div>
    )
  }
)

export const TextFieldInput = React.memo(
  (
    props: InputProps & {
      mask?: any
      label?: any
      error?: string
      className?: string
      isLoading?: boolean
      underAutoComplete?: boolean
    }
  ) => {
    const ref = useRef<any>(null)
    const [hasValue, setHasValue] = React.useState(!!props.value)
    const [hasFocus, setHasFocus] = React.useState(false)
    const [hasDisabled, setHasDisabled] = React.useState(false)

    useEffect(() => {
      if (props.mask) {
        let mask = props.mask as any
        if (mask === 'cpf') mask = { mask: '999.999.999-99' }
        else if (mask === 'cnpj') mask = { mask: '99.999.999/9999-99' }
        else if (mask === 'renach') mask = { mask: 'GO999999999' }
        else if (mask === 'telefone') {
          mask = { mask: ['(99) 9999-9999', '(99) 99999-9999'], keepStatic: true }
        } else mask = { mask }
        Inputmask(mask).mask(ref.current.input)
      }
    }, [props.mask])

    useEffect(() => {
      setHasValue(!!props.value)
      setHasDisabled(!!props.disabled)
    }, [props.value, props.disabled])

    const handleKeyDown = (e: any) => {
      props.onKeyDown?.(e)
      if (props.value === e.target.value) return
      props.onChange?.(e)
    }

    return (
      <Container
        underAutoComplete={props.underAutoComplete}
        className={`${hasFocus ? 'has-focus' : ''}${hasValue ? ' has-value' : ''}${
          hasDisabled ? ' has-disabled' : ''
        }`}
      >
        <label>{props.label}</label>
        <Error error={props.error} />
        <Input
          ref={ref}
          {...props}
          onKeyDown={handleKeyDown}
          suffix={props.suffix}
          onFocus={(e) => {
            setHasFocus(true)
            props.onFocus?.(e)
          }}
          onBlur={(e) => {
            setHasFocus(false)
            props.onBlur?.(e)
          }}
          onChange={(e) => {
            setHasValue(!!e.target.value)
            props.onChange?.(e)
          }}
        />
        <Loader isLoading={props.isLoading} />
      </Container>
    )
  }
)
const Container = styled.label<{ underAutoComplete?: boolean }>`
  position: relative;
  transition: all 0.2s ease-in-out;
  label {
    color: #484848c9;
    position: absolute;
    font-size: 14px;
    font-weight: 500;
    z-index: 1;
    top: calc(50% - 10px);
    left: 10px;
    transition: all 0.25s;
    z-index: 2;
  }
  &.has-value,
  &.has-focus {
    label {
      top: ${(props) => (props.underAutoComplete ? '-12px' : '-26px')};
      left: 10px;
      font-size: 12px;
      background-color: #fff;
      padding: 0 3px;
    }
  }
  &.has-disabled {
    label {
      background-color: #f5f5f5;
    }
  }
  input {
    height: 50px;
    border-radius: 5px;
    &:not(:disabled) {
      &:focus,
      &:hover {
        border-color: #eb8d00;
        box-shadow: 0 0 0 2px rgba(255, 186, 24, 0.2);
        ::placeholder {
          color: transparent;
        }
      }
    }
  }
`
const Loader = ({ isLoading }: { isLoading?: boolean }) => {
  if (!isLoading) return null
  const { Layout } = Loader
  return (
    <Layout>
      <TailSpin color="#292926" height={28} width={28} visible={true} />
    </Layout>
  )
}
Loader.Layout = styled.div`
  position: absolute;
  right: 10px;
  top: -7px;
`

const Error = ({ error }: { error: string | undefined }) => {
  if (!error) return null
  const { Layout, Text } = Error
  return (
    <Layout>
      <Text>{error}</Text>
    </Layout>
  )
}
Error.Layout = styled.div`
  position: absolute;
  right: 5px;
  top: -2px;
`
Error.Text = styled.span`
  font-size: 12px;
  color: red;
`
